import { useState, useEffect, useCallback } from "react";
import { Country, State, City } from "country-state-city";
import "./Members.css";

export default function Members() {
  const [members, setMembers] = useState([]);
  const [editId, setEditId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [selectedMember, setSelectedMember] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [page, setPage] = useState(1);
  const limit = 10;
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 1,
  });
  // FIX 1: Separate state for global status counts (not limited to current page)
  const [statusCounts, setStatusCounts] = useState({
    Active: 0,
    Inactive: 0,
    Pending: 0,
  });

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    gender: "",
    dob: "",
    occupation: "",
    street: "",
    city: "",
    state: "",
    country: "",
    message: "",
    registrationDate: new Date().toISOString().split("T")[0],
    membershipId: `MEM${Date.now().toString().slice(-6)}`,
    status: "Active",
  });

  const [errors, setErrors] = useState({});

  // ---------------------------------------------------------------------------
  // FIX 2: Wrap in useCallback so useEffect deps are stable & correct
  // FIX 3: Added server-side search & status filter params
  // ---------------------------------------------------------------------------
  const fetchVolunteers = useCallback(
    async (pageNo = page, search = searchTerm, status = statusFilter) => {
      try {
        const params = new URLSearchParams({
          page: pageNo,
          limit,
          ...(search && { search }),
          ...(status !== "All" && { status }),
        });

        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/members.php?${params}`
        );
        const data = await res.json();

        if (data.success === true) {
          setMembers(data.data || []);
          setPagination(data.pagination);
          setPage(pageNo);
          // FIX 1: pull global counts from the new API response field
          if (data.statusCounts) setStatusCounts(data.statusCounts);
        } else {
          setMembers([]);
        }
      } catch (err) {
        console.error("Failed to fetch members:", err);
        setMembers([]);
      }
    },
    [limit] // only `limit` is a true external dep; page/search/status are passed as args
  );

  // ---------------------------------------------------------------------------
  // FIX 4: Reset page to 1 whenever search or filter changes;
  //         trigger fetch with the new values immediately
  // ---------------------------------------------------------------------------
  useEffect(() => {
    setPage(1);
    fetchVolunteers(1, searchTerm, statusFilter);
  }, [searchTerm, statusFilter, fetchVolunteers]);

  // Re-fetch when page changes (e.g. clicking prev/next)
  useEffect(() => {
    fetchVolunteers(page, searchTerm, statusFilter);
  }, [page, searchTerm, statusFilter, fetchVolunteers]);

  // Validation function
  const validateField = (name, value) => {
    const currentYear = new Date().getFullYear();
    switch (name) {
      case "name":
        if (!value) return "Full Name is required";
        else if (value.length < 3) return "Full Name must be at least 3 characters";
        else if (/\d/.test(value)) return "Full Name cannot contain numbers";
        return "";
      case "email":
        if (!value) return "Email is required";
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
          return "Invalid email format";
        return "";
      case "phone":
        if (!value) return "Phone number is required";
        else if (!/^\d{10}$/.test(value)) return "Phone number must be 10 digits";
        return "";
      case "gender":
        if (!value) return "Please select gender";
        return "";
      case "dob":
        if (!value) return "Date of birth is required";
        else {
          const birthYear = new Date(value).getFullYear();
          if (birthYear > currentYear - 5) return "You must be at least 5 years old";
        }
        return "";
      case "occupation":
        if (!value) return "Please select occupation";
        return "";
      case "street":
        if (!value) return "Street is required";
        return "";
      case "city":
        if (!value) return "Please select city";
        return "";
      case "state":
        if (!value) return "Please select state";
        return "";
      case "country":
        if (!value) return "Please select country";
        return "";
      default:
        return "";
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "country")
      setFormData({ ...formData, country: value, state: "", city: "" });
    else if (name === "state") setFormData({ ...formData, state: value, city: "" });
    else setFormData({ ...formData, [name]: value });

    setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
  };

  // ---------------------------------------------------------------------------
  // Helpers to reset formData (DRY)
  // ---------------------------------------------------------------------------
  const blankForm = () => ({
    name: "",
    email: "",
    phone: "",
    gender: "",
    dob: "",
    occupation: "",
    street: "",
    city: "",
    state: "",
    country: "",
    message: "",
    registrationDate: new Date().toISOString().split("T")[0],
    membershipId: `MEM${Date.now().toString().slice(-6)}`,
    status: "Active",
  });

  const openCreateModal = () => {
    setFormData(blankForm());
    setEditId(null);
    setErrors({});
    setShowCreateModal(true);
  };

  const openEditModal = (member) => {
    setFormData({ ...member });
    setEditId(member.id);
    setErrors({});
    setShowCreateModal(true);
  };

  const closeModal = () => {
    setShowCreateModal(false);
    setEditId(null);
    setFormData(blankForm());
    setErrors({});
  };

  const handleSubmit = async () => {
    const requiredFields = [
      "name","email","phone","gender","dob","occupation",
      "street","city","state","country",
    ];
    const newErrors = {};
    requiredFields.forEach((f) => {
      const err = validateField(f, formData[f]);
      if (err) newErrors[f] = err;
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      alert("Please fill all required fields correctly!");
      return;
    }

    try {
      const method = editId ? "PUT" : "POST";
      const payload = editId ? { id: editId, ...formData } : { ...formData };

      const res = await fetch(`${process.env.REACT_APP_API_URL}/members.php`, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();

      if (data.success === true) {
        alert(editId ? "Member updated successfully!" : "Member added successfully!");
        fetchVolunteers(page, searchTerm, statusFilter);
        closeModal();
      } else {
        // FIX 5: surface server-side duplicate-email error to the user
        alert(data.error || "Failed to save member. Try again.");
      }
    } catch (err) {
      console.error(err);
      alert("Error connecting to server.");
    }
  };

  // ---------------------------------------------------------------------------
  // FIX 6: DELETE now sends JSON body (matches the fixed backend)
  // ---------------------------------------------------------------------------
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this member?")) return;

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/members.php`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id }),
      });

      const data = await res.json();

      if (data.success === true) {
        alert("Member deleted successfully!");
        fetchVolunteers(page, searchTerm, statusFilter);
      } else {
        alert(data.error || "Failed to delete member.");
      }
    } catch (err) {
      console.error(err);
      alert("Error connecting to server.");
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    const member = members.find((m) => m.id === id);
    if (!member) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/members.php`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...member, status: newStatus }),
      });
      const data = await res.json();
      if (data.success === true) fetchVolunteers(page, searchTerm, statusFilter);
      else alert("Failed to update status.");
    } catch (err) {
      console.error(err);
      alert("Error connecting to server.");
    }
  };

  const handleViewDetails = (member) => {
    setSelectedMember(member);
    setShowViewModal(true);
  };
  const closeViewModal = () => {
    setShowViewModal(false);
    setSelectedMember(null);
  };

  // ---------------------------------------------------------------------------
  // FIX 7: No client-side filter needed — server already returns filtered data.
  //         Keep a simple alias for the table so the rest of the JSX stays clean.
  // ---------------------------------------------------------------------------
  const filteredMembers = members;

  const occupationOptions = [
    "Student","Teacher","Engineer","Doctor","Business",
    "Government Employee","Private Employee","Self Employed",
    "Housewife","Retired","Other",
  ];

  // ---------------------------------------------------------------------------
  // FIX 8: Pagination edge-case — disable Next when totalPages <= 1 OR page >= totalPages
  // ---------------------------------------------------------------------------
  const isLastPage = pagination.totalPages <= 1 || page >= pagination.totalPages;

  return (
    <div className="members-admin">
      {/* Header with Create Button */}
      <div className="members-header">
        <div className="header-top">
          <h2>
            <span>👥</span> Member Management
          </h2>
          <button className="btn-create" onClick={openCreateModal}>
            <span>+</span> Register New Member
          </button>
        </div>
        <p className="subtitle">Manage and organize all community members</p>

        {/* Filters Section */}
        <div className="filters-section">
          <div className="search-bar">
            <input
              type="text"
              placeholder="Search members by name, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="filters">
            <div className="filter-group">
              <label>Status Filter:</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="filter-select"
              >
                <option value="All">All Status</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="Pending">Pending</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Members Stats — FIX 1: use global statusCounts */}
      <div className="members-stats">
        <div className="stat-card">
          <div className="stat-icon">👥</div>
          <div className="stat-content">
            <h3>{pagination.total}</h3>
            <p>Total Members</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <h3>{statusCounts.Active}</h3>
            <p>Active</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">⏸️</div>
          <div className="stat-content">
            <h3>{statusCounts.Inactive}</h3>
            <p>Inactive</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-content">
            <h3>{statusCounts.Pending}</h3>
            <p>Pending</p>
          </div>
        </div>
      </div>

      {/* Members Table */}
      <div className="members-list-container">
        <div className="list-header">
          <h3>All Members ({pagination.total})</h3>
          <div className="table-actions">
            <span className="results-count">
              Showing {filteredMembers.length} of {pagination.total} members
            </span>
          </div>
        </div>

        {filteredMembers.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">👤</div>
            <p>No members found</p>
            <p className="empty-subtext">
              Try a different search or register a new member
            </p>
            <button className="btn-create-empty" onClick={openCreateModal}>
              Register First Member
            </button>
          </div>
        ) : (
          <div className="members-table">
            <table>
              <thead>
                <tr>
                  <th>Member ID</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Occupation</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredMembers.map((member) => (
                  <tr key={member.id}>
                    <td className="member-id">
                      {member.membershipId || `MEM${member.id.toString().slice(-6)}`}
                    </td>
                    <td className="member-name">
                      <div className="name-text">{member.name}</div>
                      <div className="member-gender">{member.gender || "N/A"}</div>
                    </td>
                    <td className="member-email">{member.email || "N/A"}</td>
                    <td className="member-phone">{member.phone || "N/A"}</td>
                    <td className="member-occupation">
                      <span className="occupation-badge">
                        {member.occupation || "N/A"}
                      </span>
                    </td>
                    <td className="member-status">
                      <select
                        value={member.status || "Active"}
                        onChange={(e) =>
                          handleStatusChange(member.id, e.target.value)
                        }
                        className={`status-badge status-${(
                          member.status || "Active"
                        ).toLowerCase()}`}
                      >
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                        <option value="Pending">Pending</option>
                      </select>
                    </td>
                    <td className="member-actions">
                      <div className="action-buttons">
                        <button
                          className="btn-view"
                          onClick={() => handleViewDetails(member)}
                          title="View Details"
                        >
                          👁️
                        </button>
                        <button
                          className="btn-edit"
                          onClick={() => openEditModal(member)}
                          title="Edit Member"
                        >
                          ✏️
                        </button>
                        <button
                          className="btn-delete"
                          onClick={() => handleDelete(member.id)}
                          title="Delete Member"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {/* FIX 9: Pagination is now correctly OUTSIDE .members-list-container */}
      <div className="pagination">
        <button disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
          ⬅ Previous
        </button>

        <span>
          Page {page} of {Math.max(pagination.totalPages, 1)}
        </span>

        <button disabled={isLastPage} onClick={() => setPage((p) => p + 1)}>
          Next ➡
        </button>
      </div>

      {/* Create/Edit Member Modal */}
      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <h3>{editId ? "Edit Member" : "Register New Member"}</h3>
              <button className="close-btn" onClick={closeModal}>
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="modal-form">
                {/* Name & Email */}
                <div className="form-row">
                  <div className="form-group">
                    <label>Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Enter full name"
                      className={errors.name ? "error" : ""}
                    />
                    {errors.name && (
                      <span className="error-text">{errors.name}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Enter email"
                      className={errors.email ? "error" : ""}
                    />
                    {errors.email && (
                      <span className="error-text">{errors.email}</span>
                    )}
                  </div>
                </div>

                {/* Phone & DOB */}
                <div className="form-row">
                  <div className="form-group">
                    <label>Phone Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="10-digit number"
                      className={errors.phone ? "error" : ""}
                    />
                    {errors.phone && (
                      <span className="error-text">{errors.phone}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Date of Birth *</label>
                    <input
                      type="date"
                      name="dob"
                      value={formData.dob}
                      onChange={handleChange}
                      className={errors.dob ? "error" : ""}
                      max={new Date(
                        new Date().setFullYear(new Date().getFullYear() - 5)
                      )
                        .toISOString()
                        .split("T")[0]}
                    />
                    {errors.dob && (
                      <span className="error-text">{errors.dob}</span>
                    )}
                  </div>
                </div>

                {/* Gender & Occupation */}
                <div className="form-row">
                  <div className="form-group">
                    <label>Gender *</label>
                    <select
                      name="gender"
                      value={formData.gender}
                      onChange={handleChange}
                      className={errors.gender ? "error" : ""}
                    >
                      <option value="">Select Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                    {errors.gender && (
                      <span className="error-text">{errors.gender}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Occupation *</label>
                    <select
                      name="occupation"
                      value={formData.occupation}
                      onChange={handleChange}
                      className={errors.occupation ? "error" : ""}
                    >
                      <option value="">Select Occupation</option>
                      {occupationOptions.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                    {errors.occupation && (
                      <span className="error-text">{errors.occupation}</span>
                    )}
                  </div>
                </div>

                {/* Address Fields */}
                <div className="form-row">
                  <div className="form-group">
                    <label>Country *</label>
                    <select
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      className={errors.country ? "error" : ""}
                    >
                      <option value="">Select Country</option>
                      {Country.getAllCountries().map((c) => (
                        <option key={c.isoCode} value={c.isoCode}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                    {errors.country && (
                      <span className="error-text">{errors.country}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label>State *</label>
                    <select
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      className={errors.state ? "error" : ""}
                      disabled={!formData.country}
                    >
                      <option value="">Select State</option>
                      {formData.country &&
                        State.getStatesOfCountry(formData.country).map((s) => (
                          <option key={s.isoCode} value={s.isoCode}>
                            {s.name}
                          </option>
                        ))}
                    </select>
                    {errors.state && (
                      <span className="error-text">{errors.state}</span>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>City/District *</label>
                    <select
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      className={errors.city ? "error" : ""}
                      disabled={!formData.state}
                    >
                      <option value="">Select City</option>
                      {formData.country &&
                        formData.state &&
                        City.getCitiesOfState(
                          formData.country,
                          formData.state
                        ).map((c) => (
                          <option key={c.name} value={c.name}>
                            {c.name}
                          </option>
                        ))}
                    </select>
                    {errors.city && (
                      <span className="error-text">{errors.city}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Street Address *</label>
                    <input
                      type="text"
                      name="street"
                      value={formData.street}
                      onChange={handleChange}
                      placeholder="Enter street address"
                      className={errors.street ? "error" : ""}
                    />
                    {errors.street && (
                      <span className="error-text">{errors.street}</span>
                    )}
                  </div>
                </div>

                {/* Message */}
                <div className="form-group">
                  <label>Message (Optional)</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Additional information..."
                    rows="3"
                  ></textarea>
                </div>

                {/* Status */}
                <div className="form-group">
                  <label>Member Status</label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Pending">Pending</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-cancel" onClick={closeModal}>
                Cancel
              </button>
              <button className="btn-submit" onClick={handleSubmit}>
                {editId ? "Update Member" : "Register Member"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Member Details Modal */}
      {showViewModal && selectedMember && (
        <div className="modal-overlay">
          <div className="modal-container view-modal">
            <div className="modal-header">
              <h3>Member Details</h3>
              <button className="close-btn" onClick={closeViewModal}>
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="member-details">
                <div className="detail-section">
                  <h4>Personal Information</h4>
                  <div className="detail-row">
                    <span className="detail-label">Name:</span>
                    <span className="detail-value">{selectedMember.name}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Email:</span>
                    <span className="detail-value">
                      {selectedMember.email || "N/A"}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Phone:</span>
                    <span className="detail-value">
                      {selectedMember.phone || "N/A"}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Gender:</span>
                    <span className="detail-value">
                      {selectedMember.gender || "N/A"}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Date of Birth:</span>
                    <span className="detail-value">
                      {selectedMember.dob || "N/A"}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Occupation:</span>
                    <span className="detail-value">
                      {selectedMember.occupation || "N/A"}
                    </span>
                  </div>
                </div>

                <div className="detail-section">
                  <h4>Address Information</h4>
                  <div className="detail-row">
                    <span className="detail-label">Country:</span>
                    <span className="detail-value">
                      {selectedMember.country
                        ? Country.getAllCountries().find(
                            (c) => c.isoCode === selectedMember.country
                          )?.name || selectedMember.country
                        : "N/A"}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">State:</span>
                    <span className="detail-value">
                      {selectedMember.state
                        ? State.getStatesOfCountry(
                            selectedMember.country
                          )?.find(
                            (s) => s.isoCode === selectedMember.state
                          )?.name || selectedMember.state
                        : "N/A"}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">City:</span>
                    <span className="detail-value">
                      {selectedMember.city || "N/A"}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Street:</span>
                    <span className="detail-value">
                      {selectedMember.street || "N/A"}
                    </span>
                  </div>
                </div>

                <div className="detail-section">
                  <h4>Membership Information</h4>
                  <div className="detail-row">
                    <span className="detail-label">Member ID:</span>
                    <span className="detail-value">
                      {selectedMember.membershipId ||
                        `MEM${selectedMember.id.toString().slice(-6)}`}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Status:</span>
                    <span
                      className={`status-badge status-${(
                        selectedMember.status || "Active"
                      ).toLowerCase()}`}
                    >
                      {selectedMember.status || "Active"}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Registration Date:</span>
                    <span className="detail-value">
                      {selectedMember.registrationDate || "N/A"}
                    </span>
                  </div>
                </div>

                {selectedMember.message && (
                  <div className="detail-section">
                    <h4>Additional Message</h4>
                    <div className="detail-description">
                      {selectedMember.message}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-close" onClick={closeViewModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}